import React, { useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import { Badge, Button, Icon, useToast } from "stories/Admin";
import styles from "./DuaPage.module.scss";

interface DuaAudio {
  audioId: number | null;
  url: string | null;
}

interface Props {
  duaId: number;
  onBusyChange: (busy: boolean) => void;
}

const DuaAudioField: React.FC<Props> = ({ duaId, onBusyChange }) => {
  const api = useFetch<DuaAudio>();
  const execute = useRef(api.execute);
  execute.current = api.execute;
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [audio, setAudio] = useState<DuaAudio | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const endpoint = `/api/v1/duas/${duaId}/audio`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    setAudio(null);
    execute
      .current(endpoint, "GET")
      .then((result) => {
        if (!active) return;
        if (result && "audioId" in result) setAudio(result);
        else setLoadError(true);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpoint, attempt]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (
      file.size === 0 ||
      file.size > 10 * 1024 * 1024 ||
      !/\.mp3$/i.test(file.name)
    ) {
      toast.error("Velg en MP3-fil på maksimalt 10 MB.");
      return;
    }
    setBusy(true);
    onBusyChange(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await execute.current(endpoint, "POST", form);
      if (result?.url && result.audioId) {
        setAudio(result);
        toast.success("Lyden er lastet opp og knyttet til duaen.");
      } else {
        toast.error("Lyden ble ikke lagret. Prøv igjen.");
      }
    } catch {
      toast.error("Lyden ble ikke lagret. Prøv igjen.");
    } finally {
      setBusy(false);
      onBusyChange(false);
    }
  };

  return (
    <section className={styles.audioField} aria-label="Lydopptak" data-dua-id={duaId}>
      <div className={styles.audioActions}>
        <strong>Lydopptak</strong>
        {audio && (
          <Badge tone={audio.url ? "mint" : "gold"}>
            {audio.url ? "Har lyd" : "Ingen lyd"}
          </Badge>
        )}
      </div>
      {loading && <p role="status">Henter lyd …</p>}
      {loadError && (
        <div role="alert">
          <p>Kunne ikke hente lydinformasjonen.</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Prøv igjen
          </Button>
        </div>
      )}
      {audio?.url && (
        <audio
          controls
          preload="none"
          className={styles.audio}
          src={audio.url}
          aria-label="Spill av duaens lyd"
        />
      )}
      <div>
        <input
          ref={input}
          type="file"
          accept=".mp3,audio/mpeg"
          aria-label="Velg MP3-fil"
          hidden
          disabled={busy || loading || loadError}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";
            upload(file);
          }}
        />
        <Button
          size="sm"
          variant="secondary"
          loading={busy}
          disabled={loading || loadError}
          icon={<Icon name="upload" size={14} />}
          onClick={() => input.current?.click()}
        >
          {audio?.url ? "Bytt lyd" : "Last opp lyd"}
        </Button>
      </div>
      <p className={styles.audioHint}>
        MP3, maks 10 MB. Lyden lagres med en gang. Tekstendringer lagres med
        «Lagre endringer».
      </p>
    </section>
  );
};

export default DuaAudioField;
